import { createFileRoute } from '@tanstack/react-router';
import { Stepper } from '@/components/shared/Stepper';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslator } from '@/hooks/useTranslator';
import { CredentialsStep } from '../../-components/CredentialsStep';
import { ProviderStep } from '../../-components/ProviderStep';
import { RegisterAppStep } from '../../-components/RegisterAppStep';
import { SsoOnboardingShell } from '../../-components/SsoOnboardingShell';
import { TestStep } from '../../-components/TestStep';
import { useSsoOnboarding } from '../../-hooks/useSsoOnboarding';
import { useSsoOnboardingWizard } from '../../-hooks/useSsoOnboardingWizard';

/**
 * The SSO onboarding portal a customer's IT admin opens from the expiring link
 * Retraced staff send them. Public: the token in the path authorises it.
 */
export const Route = createFileRoute('/sso-onboarding/(pages)/$token/')({
  component: SsoOnboardingPage,
});

function SsoOnboardingPage() {
  const { token } = Route.useParams();
  const { getTranslation: t } = useTranslator({ page: 'ssoOnboarding' });
  const { view, isLoading } = useSsoOnboarding(token);
  const wizard = useSsoOnboardingWizard(token, view);

  if (isLoading) {
    return (
      <SsoOnboardingShell title={t('title')}>
        <Skeleton className='h-48 w-full' />
      </SsoOnboardingShell>
    );
  }

  if (!view) {
    return (
      <SsoOnboardingShell title={t('title')}>
        <p data-testid='sso-onboarding-invalid' className='text-sm'>
          {t('invalidLink')}
        </p>
      </SsoOnboardingShell>
    );
  }

  const steps = [
    { label: t('steps.provider') },
    { label: t('steps.register') },
    { label: t('steps.credentials') },
    { label: t('steps.test') },
  ];

  return (
    <SsoOnboardingShell
      title={t('titleFor', { name: view.organization.name })}
      subtitle={t('subtitle', {
        domains: view.organization.domains.join(', '),
      })}
    >
      <Stepper steps={steps} currentStep={wizard.step}>
        <div className='pt-6'>
          {wizard.step === 1 && (
            <ProviderStep
              selected={wizard.provider}
              onChoose={wizard.chooseProvider}
            />
          )}
          {wizard.step === 2 && wizard.provider && (
            <RegisterAppStep
              provider={wizard.provider}
              redirectUri={view.redirectUri}
              onBack={() => wizard.goTo(1)}
              onNext={() => wizard.goTo(3)}
            />
          )}
          {wizard.step === 3 && wizard.provider && (
            <CredentialsStep
              provider={wizard.provider}
              field={wizard.field}
              values={wizard.values}
              setField={wizard.setField}
              hasSavedSecret={wizard.hasSavedSecret}
              canSave={wizard.canSave}
              isSaving={wizard.isSaving}
              error={wizard.error}
              onBack={() => wizard.goTo(2)}
              onSave={() => void wizard.save()}
            />
          )}
          {wizard.step === 4 && (
            <TestStep
              connection={view.connection}
              isStarting={wizard.isStartingTest}
              error={wizard.error}
              onEdit={() => wizard.goTo(1)}
              onTest={() => void wizard.startTest()}
            />
          )}
        </div>
      </Stepper>
    </SsoOnboardingShell>
  );
}
